import { useEffect, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert } from "../components/ErrorAlert";
import { canvasPath, useNavigate } from "../router";

import { PresentationLessonPage } from "./PresentationLessonPage";

import type { ApiErrorCode } from "../api/client";
import type { CatalogTransportFactory } from "../catalog/catalogTransport";
import type { EditorRoute } from "../router";
import type { CanvasRecord } from "@canvaschool/contracts";
import type { ReactNode } from "react";

type Load =
  | { status: "loading" }
  | { status: "failed"; code: ApiErrorCode }
  | { status: "ready"; canvas: CanvasRecord };

/**
 * 캔버스 경로의 문 앞: 종류를 먼저 읽는다. 발표 수업은 교사에게 학생 카드
 * 화면을, 학생에게 자기 발표 캔버스를 연다. 그 밖에는 `children`(에디터).
 */
export const CanvasEntry = ({
  route,
  createCatalogTransport,
  children,
}: {
  route: EditorRoute;
  createCatalogTransport?: CatalogTransportFactory;
  /** Gets the record already read here, so the editor need not ask again. */
  children: (canvas: CanvasRecord) => ReactNode;
}) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { api } = auth;
  const { classId, canvasId } = route;
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  const session =
    auth.state.status === "authenticated" ? auth.state.session : null;
  const role = session?.principal.role;
  const ownNumber = session?.studentProfile?.studentNumber;

  useEffect(() => {
    let cancelled = false;
    setLoad({ status: "loading" });
    void (async () => {
      try {
        const canvas = await api.getCanvas(classId, canvasId);
        if (cancelled) {
          return;
        }
        if (canvas.kind === "PRESENTATION" && role === "STUDENT") {
          if (ownNumber === undefined) {
            setLoad({ status: "failed", code: "FORBIDDEN" });
            return;
          }
          const child = await api.openPresentationStudentCanvas(
            classId,
            canvasId,
            ownNumber,
          );
          if (!cancelled) {
            navigate(canvasPath(classId, child.id), { replace: true });
          }
          return;
        }
        setLoad({ status: "ready", canvas });
      } catch (failure) {
        if (!cancelled) {
          setLoad({ status: "failed", code: auth.handleError(failure) });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // `auth` changes identity with every session refresh; the load depends only
    // on the route and who is looking.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, classId, canvasId, role, ownNumber, attempt]);

  if (load.status === "failed") {
    return (
      <div className="canvaschool-lesson-body">
        <ErrorAlert code={load.code} />
        <button type="button" onClick={() => setAttempt(attempt + 1)}>
          다시 시도
        </button>
      </div>
    );
  }
  if (load.status === "loading") {
    return (
      <p className="canvaschool-lesson-body" aria-busy="true">
        불러오는 중…
      </p>
    );
  }
  if (load.canvas.kind === "PRESENTATION") {
    return (
      <PresentationLessonPage
        classId={classId}
        canvas={load.canvas}
        createCatalogTransport={createCatalogTransport}
      />
    );
  }
  return <>{children(load.canvas)}</>;
};
